<script>
import { useAdmin } from 'dashboard/composables/useAdmin';
import SearchInput from './SearchInput.vue';
import CommentsModerationFilter from './CommentsModerationFilter.vue';
import { ref } from 'vue';
import CustomIcon from 'dashboard/components/ui/CustomIcons.vue';
import Icon from 'next/icon/Icon.vue';

export default {
  name: 'CommentModerationHeader',
  components: {
    SearchInput,
    CommentsModerationFilter,
    CustomIcon,
    Icon,
  },
  props: {
    tabs: {
      type: Array,
      default: () => [],
      validator: value => {
        return value.every(tab => tab.name);
      },
    },
    headerTitle: {
      default: '',
      type: String,
    },
    buttonText: {
      default: '',
      type: String,
    },
    buttonRoute: {
      type: [String, Object],
      default: '',
    },
    showSidemenuIcon: {
      type: Boolean,
      default: true,
    },
    showNewButton: {
      type: Boolean,
      default: false,
    },
    brands: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    customClassifications: {
      type: Array,
      default: () => [],
    },
    initialTab: {
      type: String,
      default: 'Pendentes',
    },
    initialFilterValues: {
      type: Object,
      default: () => ({}),
    },
    listLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['tabChange', 'filter', 'search', 'refresh'],
  setup() {
    const { isAdmin } = useAdmin();
    const searchQuery = ref('');

    return {
      isAdmin,
      searchQuery,
    };
  },
  data() {
    return {
      showPostsFilterDisplayMenu: false,
      filter: {},
      selectedTab: this.initialTab,
      debounce: null,
      filterButtonRect: null,
    };
  },
  computed: {
    selectedTabIndex() {
      return this.tabs.findIndex(tab => tab.name === this.selectedTab);
    },
    filterDropdownStyle() {
      if (!this.filterButtonRect) return { display: 'none' };

      // Validar que temos valores válidos
      if (
        !this.filterButtonRect.bottom ||
        !this.filterButtonRect.right ||
        !this.filterButtonRect.top
      ) {
        return { display: 'none' };
      }

      const isRTL = document.dir === 'rtl';

      // Calcula espaço disponível abaixo e acima do botão
      const spaceBelow = Math.max(
        0,
        window.innerHeight - this.filterButtonRect.bottom
      );
      const spaceAbove = Math.max(0, this.filterButtonRect.top);
      const dropdownHeight = 500; // Altura estimada do dropdown

      // Decide se abre para cima ou para baixo
      const openUpwards =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      const rightPosition = Math.max(
        0,
        window.innerWidth - this.filterButtonRect.right
      );

      const styles = {
        [isRTL ? 'left' : 'right']: `${rightPosition}px`,
      };

      if (openUpwards) {
        // Abre para cima
        const bottomPosition = Math.max(
          0,
          window.innerHeight - this.filterButtonRect.top + 8
        );
        styles.bottom = `${bottomPosition}px`;
      } else {
        // Abre para baixo
        const topPosition = Math.max(0, this.filterButtonRect.bottom + 8);
        styles.top = `${topPosition}px`;
      }

      return styles;
    },
    filterContentMaxHeight() {
      if (!this.filterButtonRect) return '400px';

      const spaceBelow = window.innerHeight - this.filterButtonRect.bottom;
      const spaceAbove = this.filterButtonRect.top;
      const headerHeight = 80; // Altura do header do filtro (sort section)
      const padding = 32; // Padding total + margem de segurança

      const openUpwards = spaceBelow < 500 && spaceAbove > spaceBelow;
      const availableSpace = openUpwards ? spaceAbove : spaceBelow;

      return `${Math.max(200, Math.min(400, availableSpace - headerHeight - padding))}px`;
    },
  },
  watch: {
    initialTab: {
      handler(newTab) {
        this.selectedTab = newTab;
      },
      immediate: true,
    },
    initialFilterValues: {
      handler(newFilter) {
        this.filter = { ...newFilter };
      },
      immediate: true,
    },
  },
  mounted() {
    this.filter = { ...this.initialFilterValues };
  },
  methods: {
    handleTabChange(tabName) {
      this.selectedTab = tabName;
      this.$emit('tabChange', tabName);
    },
    openPostsFilterDisplayMenu() {
      this.showPostsFilterDisplayMenu = !this.showPostsFilterDisplayMenu;
      if (this.showPostsFilterDisplayMenu) {
        this.$nextTick(() => {
          const button = this.$refs.filterButton;
          if (button) {
            const rect = button.getBoundingClientRect();
            // Validar que o rect tem valores válidos
            if (
              rect &&
              rect.top !== undefined &&
              rect.bottom !== undefined &&
              rect.right !== undefined
            ) {
              this.filterButtonRect = rect;
            } else {
              // Fallback: fechar o menu se não conseguir obter posição
              this.showPostsFilterDisplayMenu = false;
            }
          } else {
            // Fallback: fechar o menu se não encontrar o botão
            this.showPostsFilterDisplayMenu = false;
          }
        });
      } else {
        // Limpar rect quando fechar
        this.filterButtonRect = null;
      }
    },
    onFilterChange(filter) {
      this.filter = filter;
      this.showPostsFilterDisplayMenu = false;
      this.filterButtonRect = null;
      this.$emit('filter', filter);
    },
    emitRefresh() {
      if (this.listLoading) return;
      this.$emit('refresh');
    },
    debounceSearch(e) {
      this.searchQuery = e.target.value;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(async () => {
        if (this.searchQuery.length > 2 || this.searchQuery.match(/^[0-9]+$/)) {
          this.$emit('search', this.searchQuery);
        } else {
          this.$emit('search', '');
        }
      }, 500);
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col px-4 pt-4 pb-0 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800/50"
  >
    <!-- Search Bar with improved spacing -->
    <div class="mb-6">
      <SearchInput
        :search-placeholder="$t('COMMENTS.MODERATION.SEARCH_PLACEHOLDER')"
        :search-term="searchQuery"
        @input="debounceSearch"
      />
    </div>

    <!-- Header with title and actions -->
    <div class="flex justify-between items-center mb-6">
      <h1
        class="flex items-center mb-0 text-2xl font-semibold text-slate-900 dark:text-slate-100"
      >
        <woot-sidemenu-icon v-if="showSidemenuIcon" />
        <span class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          {{ headerTitle }}
        </span>
      </h1>
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="group relative flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 ease-in-out shadow-sm hover:shadow-md border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:hover:bg-slate-100 dark:disabled:hover:bg-slate-800 disabled:hover:shadow-sm"
          :disabled="listLoading"
          :aria-label="$t('COMMENTS.MODERATION.REFRESH_ARIA')"
          :title="$t('COMMENTS.MODERATION.REFRESH_ARIA')"
          @click="emitRefresh"
        >
          <Icon
            icon="i-lucide-refresh-cw"
            class="size-4 text-slate-600 dark:text-slate-400 shrink-0 motion-safe:transition-transform"
            :class="{ 'motion-safe:animate-spin': listLoading }"
          />
          <span class="text-sm font-medium">
            {{ $t('COMMENTS.MODERATION.REFRESH') }}
          </span>
        </button>
        <!-- Modern Filter Button -->
        <div class="relative">
          <button
            ref="filterButton"
            class="group relative flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 ease-in-out shadow-sm hover:shadow-md border border-slate-200 dark:border-slate-700"
            :class="{
              'ring-2 ring-woot-500 ring-offset-1 dark:ring-offset-slate-900 bg-slate-200 dark:bg-slate-700':
                showPostsFilterDisplayMenu,
            }"
            type="button"
            :aria-label="$t('COMMENTS.MODERATION.FILTERS')"
            @click="openPostsFilterDisplayMenu"
          >
            <CustomIcon
              group="general"
              name="FILTER"
              :color="showPostsFilterDisplayMenu ? '#667085' : '#5A6169'"
            />
            <span class="text-sm font-medium">
              {{ $t('COMMENTS.MODERATION.FILTERS') }}
            </span>
            <fluent-icon
              icon="chevron-down"
              size="16"
              class="text-slate-500 dark:text-slate-400 transition-transform duration-200"
              :class="{ 'rotate-180': showPostsFilterDisplayMenu }"
            />
          </button>
          <div
            v-if="showPostsFilterDisplayMenu"
            v-on-clickaway="openPostsFilterDisplayMenu"
            class="fixed inset-0 z-[100] bg-black/5 dark:bg-black/20 backdrop-blur-[1px]"
            @click="openPostsFilterDisplayMenu"
          >
            <div
              :style="filterDropdownStyle"
              class="fixed animate-slide-down"
              @click.stop
            >
              <CommentsModerationFilter
                :initial-state="filter"
                :brands="brands"
                :labels="labels"
                :custom-classifications="customClassifications"
                :max-content-height="filterContentMaxHeight"
                @filter="onFilterChange"
              />
            </div>
          </div>
        </div>

        <!-- New Button -->
        <router-link
          v-if="showNewButton && isAdmin"
          :to="buttonRoute"
          class="button success button--fixed-top px-4 py-2 rounded-lg flex gap-2 items-center shadow-sm hover:shadow-md transition-all duration-200"
        >
          <fluent-icon icon="add-circle" size="16" />
          <span class="button__content text-sm font-medium">
            {{ buttonText }}
          </span>
        </router-link>
      </div>
    </div>

    <!-- Tabs Section -->
    <woot-tabs
      v-if="tabs.length > 0"
      class="font-semibold [&_.tabs]:p-0 remove-border"
      :index="selectedTabIndex"
    >
      <woot-tabs-item
        v-for="(tab, index) in tabs"
        :key="`tab-${index}`"
        :index="index"
        :name="tab.name"
        :count="tab?.count ?? 0"
        show-badge
        @click="handleTabChange(tab.name)"
      />
    </woot-tabs>
  </div>
</template>

<style scoped>
.remove-border {
  border-bottom-width: 0;
}

@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-down {
  animation: slide-down 0.2s ease-out;
}
</style>
